import { ChevronDown } from 'lucide-react';
import { forwardRef, useEffect, useRef, useState } from 'react';

interface DropdownOption {
  value: string;
  label: string;
  description?: string;
}

interface DropdownProps {
  label?: string;
  placeholder?: string;
  options: DropdownOption[];
  value?: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
  (
    {
      label,
      placeholder = 'Select option',
      options,
      value,
      onChange,
      error,
      hint,
      required = false,
      disabled = false,
      className = '',
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedOption = options.find(option => option.value === value);

    // Detect if a compact height class is used (h-8, h-9, h-10)
    const isCompactHeight = /\bh-[89]|h-10\b/.test(className);
    const paddingClasses = isCompactHeight ? 'px-3 py-1.5' : 'px-4 py-3';
    const textClasses = isCompactHeight ? 'text-sm' : 'text-base leading-[1.75em]';

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const handleSelect = (selectedValue: string) => {
      onChange(selectedValue);
      setIsOpen(false);
    };

    return (
      <div className={`w-full font-['Inter',sans-serif]`} ref={ref}>
        {label && (
          <label className="block text-base font-medium text-[#1F1F1F] mb-2 leading-[1.75em]">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className={`w-full ${paddingClasses} border rounded-lg transition-all duration-200 flex items-center justify-between text-left font-normal ${
              error
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)]'
            } ${
              disabled
                ? 'bg-gray-50 text-[#6E7077] cursor-not-allowed'
                : 'bg-white hover:border-[var(--brand-primary)] focus:ring-2 focus:ring-opacity-20 focus:outline-none'
            } ${className}`}
          >
            <span
              className={`${textClasses} ${selectedOption ? 'text-[#1F1F1F]' : 'text-[#6E7077]'} truncate`}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <ChevronDown
              size={16}
              className={`flex-shrink-0 ml-2 transition-transform text-[#6E7077] ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {isOpen && !disabled && (
            <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
              {options.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={`w-full px-3 py-2 text-left ${textClasses} font-normal text-[#1F1F1F] hover:bg-[var(--brand-primary-light)] hover:text-[var(--brand-primary)] first:rounded-t-lg last:rounded-b-lg transition-colors`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {error && <p className="mt-2 text-sm text-red-600 font-normal leading-[1.75em]">{error}</p>}
        {hint && !error && (
          <p className="mt-2 text-sm text-gray-500 font-normal leading-[1.75em]">{hint}</p>
        )}
      </div>
    );
  }
);

Dropdown.displayName = 'Dropdown';

export default Dropdown;
